import type { CSSProperties, ReactElement } from 'react';
import { Pause, Play, SkipBack, SkipForward } from 'lucide-react';
import { formatMmSsFromMs } from '@/lib/format-time';
import { cn } from '@/lib/cn';

const DEFAULT_LEFT_OFFSET_PX = 220;

export type PlaybackDockStatus = 'playing' | 'paused';

export type PlaybackDockProps = {
  status: PlaybackDockStatus;
  title: string;
  caption?: string;
  currentMs: number;
  durationMs: number;
  leftOffset?: number;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (ms: number) => void;
  onPrev?: () => void;
  onNext?: () => void;
};

export function PlaybackDock({
  status,
  title,
  caption,
  currentMs,
  durationMs,
  leftOffset = DEFAULT_LEFT_OFFSET_PX,
  onPlay,
  onPause,
  onSeek,
  onPrev,
  onNext,
}: PlaybackDockProps): ReactElement {
  const statusLabel = status === 'playing' ? 'LISTENING' : 'PAUSED';
  const playPauseAction = status === 'playing' ? onPause : onPlay;
  const playPauseLabel = status === 'playing' ? 'Pause' : 'Play';
  const PlayPauseIcon = status === 'playing' ? Pause : Play;
  const style: CSSProperties = { ['--left-offset' as 'left']: `${leftOffset}px` };

  return (
    <section
      aria-label="Playback"
      style={style}
      className={cn(
        'fixed inset-x-0 bottom-0 z-[60] flex items-center gap-6 bg-[var(--ink)] text-[var(--bg-surface)]',
        'px-4 py-4 shadow-2xl border-t border-[var(--rule)]',
        'md:left-[var(--left-offset)] md:right-0',
      )}
    >
      <span
        className={cn(
          'inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest',
          status === 'playing'
            ? 'bg-[var(--brand)] text-white'
            : 'bg-[var(--bg-soft)] text-[var(--ink-3)]',
        )}
      >
        {statusLabel}
      </span>

      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold">{title}</span>
        {caption && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--ink-3)]">
            {caption}
          </span>
        )}
      </div>

      <div className="flex flex-1 items-center gap-3">
        <span className="font-mono text-xs tabular-nums">
          {formatMmSsFromMs(currentMs)} / {formatMmSsFromMs(durationMs)}
        </span>
        <input
          type="range"
          aria-label="Seek"
          min={0}
          max={Math.max(durationMs, 1)}
          value={currentMs}
          onChange={(event) => onSeek(Number(event.target.value))}
          className="flex-1 accent-[var(--brand)]"
        />
      </div>

      <div className="flex items-center gap-2">
        {onPrev && (
          <button
            type="button"
            aria-label="Previous"
            onClick={onPrev}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--rule)]"
          >
            <SkipBack className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          aria-label={playPauseLabel}
          onClick={playPauseAction}
          className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[var(--brand)] text-white shadow-lg hover:opacity-90"
        >
          <PlayPauseIcon className="h-6 w-6 fill-current" />
        </button>
        {onNext && (
          <button
            type="button"
            aria-label="Next"
            onClick={onNext}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--rule)]"
          >
            <SkipForward className="h-4 w-4" />
          </button>
        )}
      </div>
    </section>
  );
}
